import * as React from "react";
import { forwardRef, Ref } from "react";
import cx from "classnames";
import { cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { FilledCircle } from "components/icons/FilledCircle";

export type ButtonProps = {
  customType?:
    | "icon"
    | "primary"
    | "secondary"
    | "danger"
    | "text"
    | "ghost_icon"
    | "primary_outlined"
    | "icon_primary_outlined"
    | "ghost"
    | "link"
    | "danger_outlined"
    | "danger_link"
    | "link_neutral";
  size?: "xxsmall" | "xsmall" | "small" | "regular" | "large";
  block?: boolean;
  isLoading?: boolean;
  active?: boolean;
  icon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const buttonVariants = cva(
  "t-inline-flex t-items-center t-justify-center t-align-middle t-whitespace-nowrap t-text-sm t-font-medium t-transition-all t-duration-200 disabled:t-pointer-events-none disabled:t-opacity-50 [&_svg]:t-pointer-events-none  t-shrink-0 [&_svg]:t-shrink-0 focus:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-purple-20 focus:t-ring-2 focus:t-ring-purple-20 focus-visible:t-ring-offset-0 focus:t-ring-offset-0 t-rounded-md t-relative t-group [&_.t-rotate-180]:t-flex [&_.t-rotate-180]:t-items-center [&_.t-rotate-180]:t-justify-center [&_svg]:t-align-middle [&_svg]:t-leading-none",
  {
    variants: {
      customType: {
        primary:
          "t-border-none t-bg-purple t-text-white hover:t-bg-purple-70 active:t-bg-purple-70 active:t-shadow-[inset_0_4px_4px_0_rgba(0,0,0,0.25)] disabled:t-bg-neutral-20 disabled:t-text-neutral-40 data-[loading=true]:t-bg-neutral-20 data-[loading=true]:t-text-purple-70",
        danger:
          "t-border-none t-bg-red t-text-white hover:t-bg-red-70 active:t-bg-black active:t-shadow-[inset_0_4px_4px_0_rgba(0,0,0,0.25)] disabled:t-bg-neutral-20 disabled:t-text-neutral-40 data-[loading=true]:t-bg-neutral-20 data-[loading=true]:t-text-purple-70",
        primary_outlined:
          "t-border t-border-solid t-border-purple t-bg-surface-transparent t-text-purple hover:t-bg-purple-0 active:t-shadow-[inset_0_4px_4px_0_rgba(86,34,255,0.08)] disabled:t-text-neutral-20 disabled:t-border-neutral-20 data-[loading=true]:t-border-neutral-20 data-[loading=true]:t-text-purple-70",
        danger_outlined:
          "t-border t-border-solid t-border-red t-bg-white t-text-red hover:t-bg-red-0 active:t-shadow-[inset_0_4px_6px_0_rgba(191,26,47,0.10)] disabled:t-text-neutral-20 disabled:t-border-neutral-20 data-[loading=true]:t-border-neutral-20 data-[loading=true]:t-text-purple-70",
        secondary:
          "t-border t-border-solid t-border-neutral-10 t-bg-white t-text-text-60 t-shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] hover:t-bg-surface-grey hover:t-border hover:t-border-solid hover:t-border-neutral-10 active:t-shadow-[inset_0_4px_4px_0_rgba(0,0,0,0.04)] disabled:t-text-neutral-50 disabled:t-bg-neutral-10 data-[loading=true]:t-bg-neutral-10 data-[loading=true]:t-text-purple-70 data-[loading=true]:t-border-none",
        icon: "t-border t-border-solid t-border-neutral-10 t-bg-white t-text-text-60 t-shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] hover:t-bg-surface-grey hover:t-border hover:t-border-solid hover:t-border-neutral-10 active:t-shadow-[inset_0_4px_4px_0_rgba(0,0,0,0.06)] disabled:t-text-neutral-20 disabled:t-bg-neutral-10 data-[loading=true]:t-bg-neutral-10 data-[loading=true]:t-text-purple-70 data-[loading=true]:t-border-none data-[size=regular]:t-w-10 data-[size=regular]:t-min-w-10 data-[size=small]:t-w-8 data-[size=small]:t-min-w-8 data-[size=large]:t-w-12 data-[size=large]:t-min-w-12 data-[size=xsmall]:t-w-7 data-[size=xsmall]:t-min-w-7 !t-px-0",
        text: "t-border-none t-bg-surface-transparent t-text-purple hover:t-bg-purple-0 active:t-shadow-[inset_0_4px_4px_0_rgba(120,78,255,0.07)] disabled:t-text-neutral-20 data-[loading=true]:t-bg-surface-transparent data-[loading=true]:t-text-purple-70 data-[loading=true]:t-border-none",
        ghost_error:
          "t-border-none t-bg-surface-transparent t-text-red hover:t-bg-red-10 active:t-shadow-[inset_0_4px_3px_0_rgba(191,26,47,0.06)] disabled:t-text-neutral-20 data-[loading=true]:t-bg-surface-transparent data-[loading=true]:t-text-red data-[loading=true]:t-border-none",
        ghost:
          "t-border-none t-bg-surface-transparent t-text-text-30 hover:t-bg-neutral-0 active:t-shadow-[inset_0_4px_6px_0_rgba(0,0,0,0.06)] disabled:t-text-neutral-20 data-[loading=true]:t-bg-surface-transparent data-[loading=true]:t-text-purple-70 data-[loading=true]:t-border-none",
        ghost_icon:
          "t-border-neutral-10 t-bg-surface-transparent !t-border-none !t-px-1 t-drop-shadow-none t-text-text-30 hover:!t-bg-neutral-0 active:!t-bg-neutral-20 disabled:t-text-neutral-20 data-[size=regular]:t-w-10 data-[size=small]:t-w-8 data-[size=large]:t-w-12 data-[size=xsmall]:t-w-7 data-[size=xsmall]:!t-h-7",
        link: "t-text-purple t-bg-surface-transparent !t-border-none !t-px-0 !t-py-0 !t-h-auto hover:t-text-purple-80 hover:t-underline disabled:!t-text-neutral-20 !t-min-h-min t-w-fit",
        danger_link:
          "t-text-red t-bg-surface-transparent !t-border-none !t-px-0 !t-py-0 !t-h-auto hover:t-text-red-80 hover:t-underline disabled:!t-text-neutral-20 !t-min-h-min t-w-fit data-[loading=true]:t-bg-surface-transparent data-[loading=true]:t-text-red data-[loading=true]:t-border-none",
        link_neutral:
          "t-text-text-30 t-bg-surface-transparent !t-border-none !t-px-0 !t-py-0 !t-h-auto hover:t-text-text-60 hover:t-underline disabled:!t-text-neutral-20 !t-min-h-min t-w-fit data-[loading=true]:t-bg-surface-transparent data-[loading=true]:t-text-purple-70 data-[loading=true]:t-border-none",
        icon_primary_outlined:
          "t-border t-border-solid t-w-fit t-border-purple t-text-purple hover:t-bg-purple-0 focus:t-bg-purple-10 disabled:t-text-purple-30 disabled:t-border-purple-10",
      },
      size: {
        default:
          "data-[size=default]:t-h-10 data-[size=default]:t-text-body-sm t-px-4",
        large:
          "data-[size=large]:t-h-12 data-[size=large]:t-text-body-lg data-[size=large]:t-font-medium t-px-5",
        regular:
          "data-[size=regular]:t-h-10 data-[size=regular]:t-text-body-sm data-[size=regular]:t-font-medium t-px-4 ",
        small:
          "data-[size=small]:t-h-8 data-[size=small]:t-text-body-sm t-px-3 data-[size=small]:t-font-medium",
        xsmall:
          "data-[size=xsmall]:t-h-7 data-[size=xsmall]:t-text-body-sm data-[size=xsmall]:t-font-medium t-px-2",
        xxsmall:
          "data-[size=xxsmall]:t-h-5 data-[size=xxsmall]:t-text-body-sm data-[size=xxsmall]:t-font-medium t-px-2",
      },
    },
    defaultVariants: {
      customType: "primary",
      size: "default",
    },
  }
);

export function LoadingDots() {
  return (
    <>
      {[0, 0.15, 0.3].map((delay, idx) => (
        <motion.span
          key={idx}
          className={idx < 2 ? "t-mr-0.5" : ""}
          initial={{ opacity: 1, y: -2 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            ease: "easeInOut",
            duration: 0.6,
            repeat: Infinity,
            repeatType: "mirror",
            delay,
          }}
        >
          <FilledCircle className="t-size-1" />
        </motion.span>
      ))}
    </>
  );
}

export const Button = forwardRef(
  (
    {
      children,
      customType = "secondary",
      size = "regular",
      block = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      disabled,
      className,
      ...props
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const strokeColors: Record<string, [string, string]> = {
      primary: ["#9A7AFF", "#5622FF"],
      danger: ["#D3606E", "#BF1A2F"],
    };

    const [startColor, endColor] = strokeColors[customType] || [];

    return (
      <button
        ref={ref}
        data-loading={isLoading}
        data-size={size}
        disabled={disabled}
        className={cx(
          buttonVariants({ customType, size }),
          { "t-w-full": block },
          className
        )}
        {...props}
      >
        {/* Gradient stroke */}
        {!disabled && !isLoading && startColor && endColor && (
          <span
            className={`t-absolute t-rounded-[6px] t-pointer-events-none group-active:t-inset-0 t-border-b-0 t-border-[1px] [-webkit-mask:linear-gradient(#fff_0_0)_content-box,_linear-gradient(#fff_0_0)] [-webkit-mask-composite:xor] [mask-composite:exclude] t-box-border t-border-solid t-border-surface-transparent t-inset-px`}
            style={{
              background: `
                     linear-gradient(to bottom, ${startColor}, ${endColor}) border-box`,
            }}
          />
        )}

        <div className="t-relative t-flex t-items-center t-justify-center sm:t-min-w-0 sm:t-max-w-full">
          <div
            className={
              isLoading
                ? "t-invisible"
                : "t-visible t-flex t-items-center t-justify-center sm:t-min-w-0 sm:t-max-w-full"
            }
          >
            {leftIcon && <span className="t-flex t-mr-2">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="t-flex t-ml-2">{rightIcon}</span>}
          </div>

          {isLoading && (
            <span className="t-absolute t-inset-0 t-flex t-items-center t-justify-center">
              <LoadingDots />
            </span>
          )}
        </div>
      </button>
    );
  }
);
